import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { UserRead } from "@/shared/api/auth";

export const RoleSlotType = ["company", "employee"] as const;
export type RoleSlot = (typeof RoleSlotType)[number];

export interface SessionData {
  token: string;
  refreshToken: string;
  user: UserRead;
}

const ACTIVE_ROLE_KEY = "heixiu_active_role";
type Suffix = "token" | "refresh_token" | "user";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

/** 续期结果：网络/服务端暂时性故障与 refresh_token 明确失效必须区分对待 */
export type RefreshSessionResult = "refreshed" | "invalid" | "network-error";

interface RefreshTokenResponse {
  code: number;
  message?: string;
  data?: {
    access_token: string;
    refresh_token: string;
  };
}

function storageKey(role: RoleSlot, suffix: Suffix): string {
  return `heixiu_${role}_${suffix}`;
}

function readActiveRole(): RoleSlot | null {
  const value = sessionStorage.getItem(ACTIVE_ROLE_KEY);
  return value === "company" || value === "employee" ? value : null;
}

function writeActiveRole(role: RoleSlot | null): void {
  if (role) sessionStorage.setItem(ACTIVE_ROLE_KEY, role);
  else sessionStorage.removeItem(ACTIVE_ROLE_KEY);
}

function loadSession(role: RoleSlot): SessionData | null {
  const token = localStorage.getItem(storageKey(role, "token"));
  const rawUser = localStorage.getItem(storageKey(role, "user"));
  if (!token || !rawUser) return null;
  try {
    return {
      token,
      refreshToken: localStorage.getItem(storageKey(role, "refresh_token")) ?? "",
      user: JSON.parse(rawUser) as UserRead,
    };
  } catch {
    return null;
  }
}

function persistSession(role: RoleSlot, session: SessionData | null): void {
  if (!session) {
    localStorage.removeItem(storageKey(role, "token"));
    localStorage.removeItem(storageKey(role, "refresh_token"));
    localStorage.removeItem(storageKey(role, "user"));
    return;
  }
  localStorage.setItem(storageKey(role, "token"), session.token);
  localStorage.setItem(storageKey(role, "refresh_token"), session.refreshToken);
  localStorage.setItem(storageKey(role, "user"), JSON.stringify(session.user));
}

function deriveRoleFromUser(user: UserRead): RoleSlot | null {
  if (user.role !== "user") return null;
  if (user.account_type === "company") return "company";
  if (user.account_type === "employee") return "employee";
  return null;
}

export const useAuthsStore = defineStore("auths", () => {
  const sessionsReference = ref<Record<RoleSlot, SessionData | null>>({
    company: null,
    employee: null,
  });
  const activeRoleReference = ref<RoleSlot | null>(readActiveRole());

  for (const role of RoleSlotType) sessionsReference.value[role] = loadSession(role);

  function setActiveRole(role: RoleSlot | null): void {
    activeRoleReference.value = role;
    writeActiveRole(role);
  }

  if (!activeRoleReference.value) {
    const path = location.pathname;
    if (path.startsWith("/employee")) setActiveRole("employee");
    else if (path.startsWith("/company")) setActiveRole("company");
  }

  const activeSession = computed(() => {
    const role = activeRoleReference.value;
    return role ? sessionsReference.value[role] : null;
  });
  const token = computed(() => activeSession.value?.token ?? null);
  const refreshToken = computed(() => activeSession.value?.refreshToken ?? null);
  const user = computed({
    get: () => activeSession.value?.user ?? null,
    set: (value: UserRead | null) => {
      const role = activeRoleReference.value;
      const session = role ? sessionsReference.value[role] : null;
      if (!role || !session || !value) return;
      sessionsReference.value = {
        ...sessionsReference.value,
        [role]: { ...session, user: value },
      };
      persistSession(role, sessionsReference.value[role]);
    },
  });

  function loginUser(accessToken: string, userInfo: UserRead, newRefreshToken?: string): void {
    const role = deriveRoleFromUser(userInfo);
    if (!role) throw new Error("该入口仅供企业用户登录");
    const session: SessionData = {
      token: accessToken,
      refreshToken: newRefreshToken ?? "",
      user: userInfo,
    };
    sessionsReference.value = { ...sessionsReference.value, [role]: session };
    persistSession(role, session);
    setActiveRole(role);
  }

  function updateTokens(accessToken: string, newRefreshToken: string, role?: RoleSlot): void {
    const targetRole = role ?? activeRoleReference.value;
    if (!targetRole) return;
    const session = sessionsReference.value[targetRole];
    if (!session) return;
    const updated = { ...session, token: accessToken, refreshToken: newRefreshToken };
    sessionsReference.value = { ...sessionsReference.value, [targetRole]: updated };
    persistSession(targetRole, updated);
  }

  // ── 续期与跨标签页同步 ──────────────────────────────────────────────────────
  // refresh_token 在后端是一次性轮换的：任何一次刷新都会让旧 token 立即失效。
  // 因此多标签页共享 localStorage 时，必须优先采用其他标签页写入的新令牌，
  // 并用 Web Locks 把续期串行化，否则后到的标签页会拿已作废的 token 刷新而误判登出。
  const refreshPromises: Partial<Record<RoleSlot, Promise<RefreshSessionResult>>> = {};

  /** 若 localStorage 中的令牌比内存新（其他标签页刚完成轮换），直接采用并返回 true。 */
  function adoptStoredSessionIfNewer(role: RoleSlot): boolean {
    const current = sessionsReference.value[role];
    if (!current) return false;
    const storedToken = localStorage.getItem(storageKey(role, "token"));
    const storedRefreshToken = localStorage.getItem(storageKey(role, "refresh_token"));
    if (!storedToken || !storedRefreshToken) return false;
    if (storedToken === current.token && storedRefreshToken === current.refreshToken) return false;
    sessionsReference.value = {
      ...sessionsReference.value,
      [role]: { ...current, token: storedToken, refreshToken: storedRefreshToken },
    };
    return true;
  }

  /** 跨标签页串行化续期；不支持 Web Locks 的浏览器退化为单页并发去重。 */
  function withRefreshLock<T>(role: RoleSlot, task: () => Promise<T>): Promise<T> {
    if (typeof navigator === "undefined" || !("locks" in navigator)) return task();
    return navigator.locks.request(`heixiu_www_refresh_${role}`, task);
  }

  /**
   * 用 refresh_token 换取新的令牌对。
   * - 同一标签页内按角色去重，并发 401 只发一次请求
   * - 跨标签页通过 Web Locks 串行；进入临界区后先采用其他标签页已写入的新令牌
   * - 网络错误 / 5xx 归类为 network-error，调用方据此保留会话而非强制登出
   */
  function refreshSession(role: RoleSlot): Promise<RefreshSessionResult> {
    const inflight = refreshPromises[role];
    if (inflight) return inflight;

    const promise = (async (): Promise<RefreshSessionResult> => {
      try {
        return await withRefreshLock(role, async (): Promise<RefreshSessionResult> => {
          if (adoptStoredSessionIfNewer(role)) return "refreshed";

          const session = sessionsReference.value[role];
          if (!session?.token || !session.refreshToken) return "invalid";

          let response: Response;
          try {
            response = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ refresh_token: session.refreshToken }),
            });
          } catch {
            return "network-error";
          }

          let json: RefreshTokenResponse;
          try {
            json = (await response.json()) as RefreshTokenResponse;
          } catch {
            return "network-error";
          }

          if (json.code === 0 && json.data?.access_token && json.data.refresh_token) {
            updateTokens(json.data.access_token, json.data.refresh_token, role);
            return "refreshed";
          }

          if (response.status >= 500) return "network-error";

          // 另一个标签页可能持有同一个旧 refresh_token 并抢先轮换成功，
          // 其写入 localStorage 的时机可能略晚于本页收到失败响应，短暂等待后复查。
          if (adoptStoredSessionIfNewer(role)) return "refreshed";
          await new Promise((resolve) => setTimeout(resolve, 200));
          if (adoptStoredSessionIfNewer(role)) return "refreshed";

          return "invalid";
        });
      } finally {
        delete refreshPromises[role];
      }
    })();

    refreshPromises[role] = promise;
    return promise;
  }

  /** 其他标签页写入/删除了某个角色的会话时，把变化同步到当前页内存。 */
  function syncSessionFromStorage(role: RoleSlot): void {
    // 只同步已存在的会话；跨标签页的「从登出到登录」交给用户在当前页主动操作
    if (!sessionsReference.value[role]) return;

    const storedToken = localStorage.getItem(storageKey(role, "token"));
    const rawUser = localStorage.getItem(storageKey(role, "user"));
    if (!storedToken || !rawUser) {
      logout(role);
      return;
    }

    let storedUser: UserRead;
    try {
      storedUser = JSON.parse(rawUser) as UserRead;
    } catch {
      return;
    }

    const session = sessionsReference.value[role];
    const storedRefreshToken = localStorage.getItem(storageKey(role, "refresh_token")) ?? "";
    if (!session || (session.token === storedToken && session.refreshToken === storedRefreshToken))
      return;
    sessionsReference.value = {
      ...sessionsReference.value,
      [role]: { token: storedToken, refreshToken: storedRefreshToken, user: storedUser },
    };
  }

  function handleStorage(event: StorageEvent): void {
    if (event.storageArea && event.storageArea !== localStorage) return;
    for (const role of RoleSlotType) {
      if (
        event.key === null ||
        event.key === storageKey(role, "token") ||
        event.key === storageKey(role, "refresh_token") ||
        event.key === storageKey(role, "user")
      ) {
        syncSessionFromStorage(role);
      }
    }
  }

  if (typeof globalThis.addEventListener === "function") {
    globalThis.addEventListener("storage", handleStorage);
  }

  function clearActiveRoleIfNoSession(): void {
    const active = activeRoleReference.value;
    if (active && sessionsReference.value[active]?.token) return;
    const remaining = RoleSlotType.find((role) => sessionsReference.value[role]?.token) ?? null;
    setActiveRole(remaining);
  }

  function logout(role?: RoleSlot): void {
    const targetRole = role ?? activeRoleReference.value;
    if (targetRole) {
      sessionsReference.value = { ...sessionsReference.value, [targetRole]: null };
      persistSession(targetRole, null);
    }
    clearActiveRoleIfNoSession();
  }

  function logoutAll(): void {
    for (const role of RoleSlotType) {
      sessionsReference.value[role] = null;
      persistSession(role, null);
    }
    setActiveRole(null);
  }

  function hasSession(role: RoleSlot): boolean {
    return Boolean(sessionsReference.value[role]?.token);
  }

  function getUserForRole(role: RoleSlot): UserRead | null {
    return sessionsReference.value[role]?.user ?? null;
  }

  const isLoggedIn = computed(() => Boolean(token.value));
  const role = computed<RoleSlot | null>(() => {
    const current = user.value;
    return current ? deriveRoleFromUser(current) : null;
  });
  const loginRoute = computed(() => "/auth" as const);

  return {
    token,
    refreshToken,
    user,
    isLoggedIn,
    role,
    loginRoute,
    loginUser,
    updateTokens,
    refreshSession,
    logout,
    logoutAll,
    sessionsRef: sessionsReference,
    activeRoleRef: activeRoleReference,
    activeSession,
    hasSession,
    getUserForRole,
    setActiveRole,
    clearActiveRoleIfNoSession,
  };
});

export type AuthsStore = ReturnType<typeof useAuthsStore>;
